import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface LanguageSelectorProps {
  /** Whether this is for mobile menu (affects styling) */
  isMobile?: boolean;
  /** Callback when language changes (optional, for closing mobile menu) */
  onLanguageChange?: () => void;
}

/**
 * Language selector component with dropdown
 *
 * Provides a dropdown menu to switch between available languages (English and Spanish).
 * Supports both desktop and mobile layouts with appropriate styling.
 *
 * @param {boolean} isMobile - Whether to use mobile styling
 * @param {Function} onLanguageChange - Optional callback when language changes
 * @returns {JSX.Element} The language selector dropdown component
 *
 * @example
 * // Desktop usage
 * <LanguageSelector />
 *
 * @example
 * // Mobile usage with callback
 * <LanguageSelector isMobile onLanguageChange={closeMobileMenu} />
 */
export function LanguageSelector({
  isMobile = false,
  onLanguageChange,
}: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "es", name: "Español", flag: "🇪🇸" },
  ];

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  /**
   * Handles language change
   *
   * @param {string} langCode - The language code to switch to (e.g., 'en', 'es')
   */
  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
    if (onLanguageChange) {
      onLanguageChange();
    }
  };

  if (isMobile) {
    return (
      <div className="px-6 py-4 border-t border-gray-100">
        <div className="text-xs text-gray-500 mb-2 font-medium">
          Language / Idioma
        </div>
        <div className="flex flex-col space-y-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                i18n.language === lang.code
                  ? "bg-violet-50 text-violet-700"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span>{lang.name}</span>
              {i18n.language === lang.code && (
                <svg
                  className="ml-auto w-5 h-5 text-violet-700"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-50"
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <span className="text-base">{currentLanguage.flag}</span>
        <span className="hidden sm:inline font-medium">
          {currentLanguage.code.toUpperCase()}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors cursor-pointer ${
                i18n.language === lang.code
                  ? "bg-violet-50 text-violet-700 font-medium"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span>{lang.name}</span>
              {i18n.language === lang.code && (
                <svg
                  className="ml-auto w-4 h-4 text-violet-700"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
